import { Component } from "react"
import PropTypes from "prop-types"

import Header from "../header"
import Description from "../description"
import LearnMoreButton from "shared/components/learn_more_button"
import chevronLeftBlack from "images/layout/chevron-left-black.svg"
import UpgradeToProModal from "app/components/upgrade_to_pro_modal"
import CheckoutModal from "app/components/upgrade_to_pro_modal/checkout_modal.tsx"

import ConnectionStoredFieldsForm from "./connection_stored_fields_form"
import { Activity, RecentActivity } from "./activity"
import Footer from "./footer"
import PushNotifications from "./push_notifications"
import { deleteLabel } from "../utils"
import { storedFieldOwner } from "shared/lib/format_stored_field"

import { deleteLiveAppletMutation } from "./api"
import { rejectedXHRPromiseLogger } from "shared/lib/xhr_promise"
import isEmpty from "lodash/isEmpty"

import logIFTTTNextAction from "app/lib/ifttt_next_log_action"
import { clearState } from "shared/localstorage_helper"

import appletOrConnection from "shared/lib/use_applet_or_connection"
import { capitalize } from "humanize-plus"

import {
  attachNewBadgeForMultiServiceAccountButton,
  removeMultiServiceAccountNewBadge,
} from "shared/scripts/new_badge_button"

import Cookies from "js-cookie"

export default class ConnectionConfig extends Component {
  configType = appletOrConnection(this.props.connection)

  state = {
    enabled: false,
    isSaving: false,
    pushEnabled: !!this.props.connection.push_enabled,
    name: this.props.connection.name,
    didChangeSetting: false,
    upgradeToPro: {
      showModal: false,
      step: "queries",
    },
    currentTier: this.props.user.tier,
    permissions: this.props.user.permissions,
    userAllowMultipleLiveChannels:
      this.props.user.userAllowMultipleLiveChannels,
    multipleLiveChannelsNewBadgeVisibility:
      this.props.user.permissions.multi_service_account.permitted === true &&
      attachNewBadgeForMultiServiceAccountButton(),
    showingCheckoutModal: false,
    checkoutPlanTarget: "intermediate_pro",
  }

  appletFeatureFlags = {
    pro_features: this.props.connection.pro_features,
    intermediate_pro_features: this.props.connection.intermediate_pro_features,
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.handleBeforeUnload, {
      once: true,
    })
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload, {
      once: true,
    })
  }

  handleBeforeUnload = event => {
    if (this.state.didChangeSetting) {
      event.returnValue =
        "You have unsaved changes. Do you want to leave this page and discard your changes?"
    }
  }

  formatAppletStepToStep(type) {
    return appletStep => {
      const fields = appletStep.step.fields
        .map(stepField => {
          const appletStepField = appletStep.fields.find(
            f => f.name === stepField.name
          )
          const storedField = this.props.formFields.storedFields[""].find(
            sf =>
              sf.name === stepField.name &&
              sf.owner ===
                storedFieldOwner(type, appletStep.step.full_module_name)
          )
          return {
            ...stepField,
            hidden: appletStepField?.hidden,
            value: storedField?.value || storedField?.default_value,
          }
        })
        .reduce((acc, cur) => ({ ...acc, [cur.name]: cur }), {})

      return {
        ...appletStep.step,
        fields,
        full_module_name: appletStep.step.full_module_name,
      }
    }
  }

  onSaveClicked = () => {
    this.setState({ didChangeSetting: false, isSaving: true })
    const { name, pushEnabled } = this.state
    this.storedFieldsForm.onSubmit(name, pushEnabled)
  }

  onDoneSaving = enabled => {
    this.setState({ isSaving: false, enabled })
  }

  onSaveName = name => {
    this.setState({ name, didChangeSetting: true })
  }

  onFieldChanged = automated => {
    if (!automated) {
      this.setState({ didChangeSetting: true })
    }
  }

  clearLocalStorage = () => {
    const localStorageId = `connectionId-${this.props.connection.id}-${this.props.sessionId}`
    clearState(localStorageId)
  }

  onClickBackButton = e => {
    e.preventDefault()

    this.clearLocalStorage()
    window.history.back()
  }

  onDeleteClicked = () => {
    logIFTTTNextAction("click", {
      location_type: "connection_config",
      object_id: this.props.connection.id,
      object_type: "connection_delete_confirm",
    })

    const success = () => {
      this.clearLocalStorage()

      Cookies.remove(
        `mobile_alert_${this.props.formFields.user.login}_${this.props.connection.id}`
      )

      window.appendFlash(`${capitalize(this.configType)} was archived`)
      window.Turbolinks.visit(this.props.urls.myAppletURL)
    }

    const fail = rejectedXHRPromiseLogger(() => {
      window.appendFlash(
        `There was an error archiving this ${this.configType}`,
        "danger"
      )
    })

    deleteLiveAppletMutation(this.props.connection.id).then(success).catch(fail)
  }

  onFormEnabled = () => {
    this.setState({ enabled: true })
  }

  onFormDisabled = () => {
    this.setState({ enabled: false })
  }

  onPushChange = pushEnabled => {
    logIFTTTNextAction("click", {
      location_type: "connection_config",
      object_id: this.props.connection.id,
      object_type: `connection_${
        pushEnabled ? "enable" : "disable"
      }_push_notification`,
    })

    this.setState({ pushEnabled, didChangeSetting: true })
  }

  hideIngredientNamespaces = () => {
    const { storedFields } = this.props.formFields
    const namespacedIngredientRegex = /{{[^{}]+\\\.[^{}]+}}/
    return Object.values(storedFields)
      .flat()
      .filter(sf => sf.field_type === "text")
      .map(sf => [sf.value, sf.default_value])
      .flat()
      .some(value => value && value.match(namespacedIngredientRegex))
  }

  proFeatureGate = async permissionName => {
    if (this.state.permissions[permissionName]?.permitted) {
      return Promise.resolve()
    } else {
      this.setState({
        upgradeToPro: {
          showModal: true,
          step: permissionName,
        },
      })
      return Promise.reject("cancellation")
    }
  }

  updatePermissionsAfterUpgrade = tier => {
    const isPro = tier === "pro"

    this.setState({
      multipleLiveChannelsNewBadgeVisibility: true,
      currentTier: tier,
      permissions: {
        ...this.state.permissions,
        filter_code: {
          ...this.state.permissions.filter_code,
          permitted: isPro,
        },
        multi_action: {
          ...this.state.permissions.multi_action,
          permitted: true,
        },
        queries: {
          ...this.state.permissions.queries,
          permitted: isPro,
        },
        multi_service_account: {
          ...this.state.permissions.multi_service_account,
          permitted: isPro,
        },
      },
    })
  }

  render() {
    const isStaticConfig = this.props.connection.config_type === "static"

    return (
      <>
        <div className="config-header">
          <button
            type="submit"
            className="button-tertiary button-outlined button-with-icon only-icon-on-mobile connection-edit-back-button"
            aria-label="Back button"
            onClick={this.onClickBackButton}
            data-track-ifttt-next-event="click"
            data-track-data={JSON.stringify({
              object_type: "back_link",
              location_type: "connection_config",
            })}
          >
            <span
              className="button-icon"
              dangerouslySetInnerHTML={{
                __html: chevronLeftBlack,
              }}
            />
            <span className="hide-on-xs">Back</span>
          </button>
          <h2 className="settings-heading">
            {`${capitalize(this.configType)} settings`}
          </h2>
          {isStaticConfig && (
            <LearnMoreButton {...this.props.header.learnMoreButton} />
          )}
        </div>
        <div className="connection-config-editor">
          <Header
            connection={{ ...this.props.connection, brand_color: "#fff" }}
            mainService={this.props.mainService}
            authorInfo={this.props.authorInfo}
            icons={this.props.icons}
            programmable={this.props.programmable}
            includeEditableTitle={this.props.includeEditableTitle}
            onSaveName={this.onSaveName}
          />
          {this.props.connection.description && (
            <Description text={this.props.connection.description} />
          )}
          <RecentActivity connection={this.props.connection} />
          {this.props.connection.can_push_enable && (
            <PushNotifications
              onChange={this.onPushChange}
              pushEnabled={this.state.pushEnabled}
            />
          )}
          <Activity
            connection={this.props.connection}
            activityURL={this.props.urls.activityURL}
            checkConnectionURL={this.props.urls.checkConnectionURL}
          />
          {!isEmpty(this.props.formFields.storedFields) && (
            <>
              <hr />

              <div className="connection-fields-form">
                <ConnectionStoredFieldsForm
                  ref={r => (this.storedFieldsForm = r)}
                  onFormEnabled={this.onFormEnabled}
                  onFormDisabled={this.onFormDisabled}
                  applet={this.props.connection}
                  submitUrl={this.props.urls.updateURL}
                  connectionURL={this.props.urls.connectionURL}
                  userAllowMultipleLiveChannels={
                    this.state.permissions.multi_service_account.permitted ===
                    true
                  }
                  proFeatureGate={this.proFeatureGate}
                  connectionFinishedUrl={this.props.urls.connectionFinishedUrl}
                  clearLocalStorage={this.clearLocalStorage}
                  programmable={this.props.programmable}
                  hideIngredientNamespaces={this.hideIngredientNamespaces()}
                  showFilterCta={false}
                  toggleShowFilterCode={() => {}}
                  filterCode=""
                  onFieldChanged={this.onFieldChanged}
                  onDoneSaving={this.onDoneSaving}
                  multipleLiveChannelsNewBadgeVisibility={
                    this.state.multipleLiveChannelsNewBadgeVisibility
                  }
                  hideMultipleLiveChannelsNewBadge={() => {
                    this.setState({
                      multipleLiveChannelsNewBadgeVisibility: false,
                    })
                    removeMultiServiceAccountNewBadge()
                  }}
                  {...this.props.formFields}
                />
              </div>
            </>
          )}
          {/*
            Conditionally load UpgradeToProModal (it's only relevant for
            Applets aka static config, not Connections aka dynamic config)
          */}
          {isStaticConfig && (
            <>
              <UpgradeToProModal
                visible={this.state.upgradeToPro.showModal}
                step={this.state.upgradeToPro.step}
                // ⚠️ these permissions need to match SUCCESSFUL_PRO_PLUS_UPGRADE
                // in app/assets/javascripts/app/components/diy_composer/reducer.js
                onUpgrade={() => {
                  this.updatePermissionsAfterUpgrade("pro")
                }}
                showCheckoutModal={target => {
                  this.setState({
                    checkoutPlanTarget: target,
                    showingCheckoutModal: true,
                  })
                }}
                onClose={() =>
                  this.setState({
                    upgradeToPro: {
                      showModal: false,
                      step: "queries",
                    },
                  })
                }
                appletsInUse={
                  this.props.user.appletQuota.total -
                  this.props.user.appletQuota.remaining
                }
                appletQuotaTotal={this.props.user.appletQuota.total}
                eligibleForTrial={this.props.user.eligibleForTrial}
                currentTier={this.state.currentTier}
                subscriptionPaymentType={
                  this.props.user.subscriptionPaymentType
                }
                proPlusProductId={this.props.proPlusProductId}
                appletFeatureFlags={this.appletFeatureFlags}
                isTrialing={this.props.user.isTrialing}
                trackLocationType="connection_config"
              />
              <CheckoutModal
                visible={this.state.showingCheckoutModal}
                target={this.state.checkoutPlanTarget}
                onClose={() => this.setState({ showingCheckoutModal: false })}
                onUpgrade={() =>
                  this.updatePermissionsAfterUpgrade(
                    this.state.checkoutPlanTarget
                  )
                }
                locationType="diy_composer"
                newProSubscriptionUrl={this.props.urls.newProSubscriptionUrl}
                newProPlusSubscriptionUrl={
                  this.props.urls.newProPlusSubscriptionUrl
                }
                modalSubscriptionUrl={this.props.urls.modalSubscriptionUrl}
              />
            </>
          )}
          <Footer
            deleteLabel={deleteLabel(this.props.connection, this.props.user)}
            applet={this.props.connection}
            enabled={this.state.enabled}
            isSaving={this.state.isSaving}
            includeSubmit={!isEmpty(this.props.formFields.storedFields)}
            onDeleteClicked={this.onDeleteClicked}
            onSaveClicked={this.onSaveClicked}
          />
          <div className="breath-space" />
          <div className="connection-id">
            ID <span>{this.props.connection.id}</span>
          </div>
        </div>
      </>
    )
  }
}

ConnectionConfig.propTypes = {
  includeEditableTitle: PropTypes.bool,
  formFields: PropTypes.shape({
    storedFields: PropTypes.objectOf(PropTypes.array),
    configuration: PropTypes.objectOf(
      PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        icon: PropTypes.string,
      })
    ),
    disabled_slugs: PropTypes.arrayOf(PropTypes.string),
    error: PropTypes.object,
    user: PropTypes.object,
    ingredientsMetadata: PropTypes.object,
    includeDescriptions: PropTypes.bool,
    includeHeader: PropTypes.bool,
    includeFooter: PropTypes.bool,
    useInlineErrors: PropTypes.bool,
    onFormEnabled: PropTypes.func,
    onFormDisabled: PropTypes.func,
    showSuccessMessage: PropTypes.bool,
    useIngredientsForAppletOwners: PropTypes.bool,
  }),
  urls: PropTypes.shape({
    activityURL: PropTypes.string,
    checkConnectionURL: PropTypes.string,
    myAppletURL: PropTypes.string,
    connectionURL: PropTypes.string,
    updateURL: PropTypes.string,
    connectionFinishedUrl: PropTypes.string,
    newProSubscriptionUrl: PropTypes.string,
    newProPlusSubscriptionUrl: PropTypes.string,
    modalSubscriptionUrl: PropTypes.string,
  }),
  header: PropTypes.shape({
    backButton: PropTypes.object,
    learnMoreButton: PropTypes.object,
  }),
  sessionId: PropTypes.string.isRequired,
  connection: PropTypes.object,
  programmable: PropTypes.bool,
  icons: PropTypes.object,
  mainService: PropTypes.object.isRequired,
  authorInfo: PropTypes.object.isRequired,
  proPlusProductId: PropTypes.string,
  user: PropTypes.shape({
    login: PropTypes.string.isRequired,
    tier: PropTypes.string,
    appletQuota: PropTypes.shape({
      remaining: PropTypes.number,
      total: PropTypes.number,
    }),
    eligibleForTrial: PropTypes.bool,
    subscriptionPaymentType: PropTypes.string,
    isTrialing: PropTypes.bool,
    userAllowMultipleLiveChannels: PropTypes.bool,
    permissions: PropTypes.objectOf(
      PropTypes.shape({
        permitted: PropTypes.bool.isRequired,
        minimumTier: PropTypes.string,
      })
    ),
  }),
}
